/**
 * Parser subjectów NATS dla powłoki (activity feed, Living Network).
 *
 * DLACZEGO osobny moduł i dlaczego przepisany: poprzedni parser mieszkał
 * wewnątrz `useNatsActivity` i zakładał JEDEN kształt subjectu —
 * `tenant.{id}.{kategoria}.{źródło}.{akcja}`. Kanoniczny słownik
 * (packages/types/src/nats.ts) ma ich kilka, a telemetria wstawia dodatkowy
 * segment `device`:
 *
 *   NATS_SUBJECTS.telemetry → tenant.{t}.telemetry.device.{deviceId}.{signal}
 *   NATS_SUBJECTS.alert     → tenant.{t}.alerts.{type}
 *   NATS_SUBJECTS.event     → tenant.{t}.events.{module}.{event}
 *   NATS_SUBJECTS.system    → system.{component}.{event}
 *
 * Przez to dla telemetrii „źródłem" stawał się literał `device`, a `device_id`
 * lądował w środku akcji (`NS-001.temperature`) — czyli w praktyce ginął.
 * Bramka forwarduje dodatkowo kanały `status` i `battery`
 * (services/websocket-gateway/src/index.ts, VALID_CHANNELS) w kształcie
 * `tenant.{t}.{status|battery}.device.{deviceId}` — one też nie miały obsługi.
 *
 * Wydzielenie do `lib/` pozwala testować parser bez montowania Reacta.
 */

export type NatsCategory = 'telemetry' | 'alerts' | 'events' | 'modules' | 'system' | 'unknown';

export interface ParsedNatsSubject {
  category: NatsCategory;
  /** Logiczny nadawca: slug modułu, identyfikator urządzenia lub komponent systemowy. */
  source: string;
  /** Nazwa zdarzenia / sygnału, bez segmentów routingu. */
  action: string;
  tenantId?: string;
  /** Ustawiane wyłącznie dla subjectów urządzeniowych — to jest naprawiany `device_id`. */
  deviceId?: string;
  /** Sygnał telemetryczny (`temperature`, `humidity`, `status`, `battery`, …). */
  signal?: string;
}

/**
 * Zdejmuje kanoniczny prefiks urządzeniowy `device.{deviceId}`.
 * Zwraca resztę segmentów, żeby wywołujący sam zdecydował, czym ona jest.
 */
function takeDeviceSegment(tail: string[]): { deviceId?: string; rest: string[] } {
  if (tail[0] === 'device' && tail.length >= 2) {
    return { deviceId: tail[1], rest: tail.slice(2) };
  }
  return { rest: tail };
}

export function parseNatsSubject(subject: string): ParsedNatsSubject {
  // Bez odsiewania pustych tokenów — parser jest pozycyjny, a NATS i tak nie
  // dopuszcza pustych segmentów; usuwanie ich przesuwałoby indeksy.
  const parts = subject.split('.');

  if (parts.length === 0 || parts[0] === '') {
    return { category: 'unknown', source: 'unknown', action: subject };
  }

  // system.{component}.{event} — subjecty spoza przestrzeni tenanta
  // (np. system.superadmin.feed, system.openclaw.update_available).
  if (parts[0] === 'system') {
    return {
      category: 'system',
      source: parts[1] ?? 'system',
      action: parts.slice(2).join('.') || 'event',
    };
  }

  if (parts[0] !== 'tenant' || parts.length < 3) {
    return { category: 'unknown', source: parts[0], action: subject };
  }

  const tenantId = parts[1];
  const channel = parts[2];
  const tail = parts.slice(3);

  switch (channel) {
    case 'telemetry': {
      const { deviceId: prefixed, rest } = takeDeviceSegment(tail);
      // Bez segmentu `device` przyjmujemy starszy kształt
      // `tenant.{t}.telemetry.{deviceId}.{signal}` — nadal produkowany przez
      // część publisherów i przez dane demonstracyjne.
      const deviceId = prefixed ?? rest[0];
      const signalParts = prefixed ? rest : rest.slice(1);
      const signal = signalParts.join('.') || 'reading';
      return {
        category: 'telemetry',
        tenantId,
        deviceId,
        signal,
        source: deviceId ?? 'unknown',
        action: signal,
      };
    }

    // tenant.{t}.status.device.{deviceId} / tenant.{t}.battery.device.{deviceId}
    // (services/neurosense-gateway-mqtt/src/event-mapper.ts, getNatsSubject).
    case 'status':
    case 'battery': {
      const { deviceId: prefixed, rest } = takeDeviceSegment(tail);
      const deviceId = prefixed ?? rest[0];
      return {
        category: 'telemetry',
        tenantId,
        deviceId,
        signal: channel,
        source: deviceId ?? 'unknown',
        action: channel,
      };
    }

    case 'alerts': {
      const { deviceId, rest } = takeDeviceSegment(tail);
      if (deviceId) {
        // tenant.{t}.alerts.device.{deviceId}[.{type}]
        return {
          category: 'alerts',
          tenantId,
          deviceId,
          source: deviceId,
          action: rest.join('.') || 'alert',
        };
      }
      if (tail.length >= 2) {
        // Kształt z jawnym segmentem źródła: tenant.{t}.alerts.{source}.{type}
        return { category: 'alerts', tenantId, source: tail[0], action: tail.slice(1).join('.') };
      }
      // Kanoniczne NATS_SUBJECTS.alert: tenant.{t}.alerts.{type} — brak nadawcy
      // w subjeccie, więc przypisujemy alert do systemu.
      return { category: 'alerts', tenantId, source: 'system', action: tail[0] ?? 'alert' };
    }

    case 'events':
    case 'modules': {
      return {
        category: channel === 'events' ? 'events' : 'modules',
        tenantId,
        source: tail[0] ?? 'system',
        action: tail.slice(1).join('.') || channel,
      };
    }

    // tenant.{t}.system.{component}.{event} — publikowane przez bramkę
    // (client_connected / client_disconnected).
    case 'system': {
      return {
        category: 'system',
        tenantId,
        source: tail[0] ?? 'system',
        action: tail.slice(1).join('.') || 'event',
      };
    }

    default:
      return {
        category: 'unknown',
        tenantId,
        source: tail[0] ?? channel,
        action: tail.slice(1).join('.') || channel,
      };
  }
}
